/**
 * Signaling wire protocol types.
 *
 * IMPORTANT: This file MUST NOT import any browser API (window, document,
 * WebSocket, RTCPeerConnection, etc.). It is the Rust/WASM migration target.
 * All fields use snake_case to match the server wire format and future Rust
 * serde models 1:1.
 */

// ── Transport descriptor (inside session_bootstrap) ───────────────────────

export interface WireIceServer {
  urls: string[];
  username?: string;
  credential?: string;
  credential_type?: string;
  ttl_ms?: number;
}

export interface WireConnectEndpoint {
  uri: string;
}

export interface WireNetworkAccess {
  ice_servers: WireIceServer[];
  connect_endpoints: WireConnectEndpoint[];
}

export interface WireTransportDescriptor {
  family: 'interactive' | 'stream_socket';
  priority: number;
  network_access: WireNetworkAccess;
}

// ── SDK → Signaling Server ─────────────────────────────────────────────────

export interface SessionOpenMsg {
  type: 'session_open';
  request_id: string;
  session_id: string;
  target_device_serial: string;
  target_channel_id: string;
  mode: 'live' | 'playback';
  /**
   * Transport family for this session.
   * 'interactive' = WebRTC P2P (single-viewer lock enforced by server).
   * 'stream_socket' = JT/T1078 relay (multi-viewer allowed; no lock).
   * Omit for auto-select based on device capability.
   */
  preferred_family?: 'interactive' | 'stream_socket';
  quality_hint?: 'uhd' | 'hd' | 'sd' | 'auto';
  time_range?: { start_ts_ms: number; end_ts_ms: number };
}

export interface SessionStartMsg {
  type: 'session_start';
  session_id: string;
  device_serial: string;
  channel_id: string;
  mode: 'live' | 'playback';
  protocol: 'p2p' | 'stream_socket';
}

export interface SessionStopMsg {
  type: 'session_stop';
  session_id: string;
}

export interface SdpAnswerMsg {
  type: 'sdp_answer';
  session_id: string;
  sdp: string;
}

export interface IceCandidateMsg {
  type: 'ice_candidate';
  session_id: string;
  candidate: string;
  sdp_mid?: string | null;
  sdp_mline_index?: number | null;
}

export type ToServerMsg =
  | SessionOpenMsg
  | SessionStartMsg
  | SessionStopMsg
  | SdpAnswerMsg
  | IceCandidateMsg
  | QosReportMsg
  | SessionTakeoverResponseMsg
  | SessionHeartbeatMsg;

// ── Signaling Server → SDK ─────────────────────────────────────────────────

/**
 * Per-channel audio capability, sourced from the device's own
 * `capability_report` and cached by signaling
 * (`vgps-signaling-golang/internal/signaling/bootstrap.go:171-188`).
 *
 * Present only when signaling actually has an answer for THIS channel — there
 * is no camera-level fallback, so asking about an unknown channel yields
 * nothing rather than a neighbouring channel's answer
 * (`internal/signaling/audio_capability_test.go:39-46`).
 */
export interface WireAudioCapability {
  available: boolean;
  codec?: string;
  sample_rate?: number;
  channels?: number;
}

/**
 * The one correct way to read the audio capability out of a bootstrap.
 *
 * Uses a presence test rather than truthiness, because `{available:false}` and
 * a missing key are different answers. Lifted from the reference harness
 * (`scripts/verify-audio.mjs:324-333`, `formatAudioCapability`), which is the
 * only place in the tree that got this right before now — every caller should
 * use this rather than re-deriving the test and getting it subtly wrong.
 *
 * Returns `undefined` for ABSENT, so callers must distinguish it from
 * `{available:false}` explicitly and cannot do so by accident.
 */
export function readAudioCapability(
  msg: Partial<SessionBootstrapMsg>,
): WireAudioCapability | undefined {
  if (!Object.prototype.hasOwnProperty.call(msg, 'audio')) return undefined;
  const audio = (msg as { audio?: unknown }).audio;
  if (!audio || typeof audio !== 'object') return undefined;
  return audio as WireAudioCapability;
}

export interface SessionBootstrapMsg {
  type: 'session_bootstrap';
  profile_id: string;
  request_id: string;
  issued_at_ms: number;
  expires_at_ms: number;
  target: { device_serial: string; channel_id: string };
  transports: WireTransportDescriptor[];
  /**
   * MUST stay optional. `{available:false}` and "key absent" are different
   * answers — device-says-no versus nobody-has-asked-yet — and collapsing them
   * into a boolean makes the three-state contract inexpressible. Read it with
   * a presence test (`'audio' in msg`), never with truthiness.
   *
   * Nothing validates this at runtime: bootstrap frames are a plain
   * `JSON.parse` cast to this interface, with no schema check on either side
   * (`bootstrap.go:182-185`).
   */
  audio?: WireAudioCapability;
}

export interface SdpOfferMsg {
  type: 'sdp_offer';
  session_id: string;
  sdp: string;
}

export interface SessionErrorMsg {
  type: 'session_error';
  session_id: string;
  request_id?: string;
  code: string;
  message: string;
  target?: { device_serial?: string; device_id?: string; channel_id?: string };
}

export type FromServerMsg = SessionBootstrapMsg | SdpOfferMsg;

// ── SDK → Signaling Server (QoS / takeover) ───────────────────────────────

/**
 * QoS telemetry uplink — SDK → Signaling Server.
 * Sent periodically so the server can aggregate multi-viewer state (JT/T1078)
 * and decide whether to issue quality_hint.
 * SDK MUST NOT directly command encoder state; server is the single writer.
 */
export interface QosReportMsg {
  type: 'qos_report';
  session_id: string;
  device_serial: string;
  channel_id: string;
  /** Transport family for this session. */
  family: 'interactive' | 'stream_socket';
  /** SDK-local QoS classification result. */
  state: 'good' | 'degraded' | 'poor';
  metrics: {
    packet_loss_ratio?: number;
    rtt_ms?: number;
    frames_per_second?: number;
    jitter_ms?: number;
    available_incoming_bitrate_kbps?: number;
    encoder_qp?: number;
    actual_fps?: number;
    applied_profile?: string;
    /** Epoch ms when metric was measured; used by server for freshness evaluation. */
    timestamp_ms?: number;
  };
  /**
   * SDK's local ABR intent for this poll cycle.
   * Server MAY use this as a hint to fast-path its own ABR decision,
   * but server remains the final authority and may override or ignore it.
   * Especially for JT/T1078 multi-viewer, server aggregates all viewers before acting.
   */
  abr_hint?: {
    action: 'adjust_params' | 'switch_rendition';
    rendition?: string;
    bitrate_kbps?: number;
    fps?: number;
    tier?: 'primary' | 'secondary';
    reason: 'congestion' | 'recovery';
    level: 1 | 2 | 3;
  };
}

/**
 * Takeover response — SDK → Signaling Server (interactive family only).
 * Sent after the user confirms or cancels a takeover prompted by session_conflict.
 * accept=true  → server revokes incumbent lock, grants session to challenger.
 * accept=false → challenger backs off, incumbent is unaffected.
 */
export interface SessionTakeoverResponseMsg {
  type: 'session_takeover_response';
  session_id: string;
  device_serial: string;
  channel_id: string;
  accept: boolean;
}

/**
 * Heartbeat keepalive — SDK → Signaling Server (interactive family only).
 * Sent every ~15 s to keep the interactive session lock alive.
 * Server expires the lock if no heartbeat is received within LOCK_LEASE_S seconds,
 * releasing zombie locks from crashed/killed browser tabs.
 */
export interface SessionHeartbeatMsg {
  type: 'session_heartbeat';
  session_id: string;
  device_serial: string;
  channel_id: string;
}

// ── Signaling Server → SDK (QoS / conflict / capability) ──────────────────

/**
 * Session conflict — Signaling Server → SDK (interactive family).
 * Emitted when server detects an existing interactive lock on deviceSerial+channelId.
 * The challenged (incumbent) viewer also receives this with role='incumbent'.
 * UI must prompt for takeover. See docs/qos/design-qos.md § Interactive takeover.
 */
export interface SessionConflictMsg {
  type: 'session_conflict';
  session_id: string;
  device_serial: string;
  channel_id: string;
  family: 'interactive';
  /** 'challenger' = this client requested the conflicting session_open.
   *  'incumbent'  = existing viewer that is being challenged. */
  role: 'challenger' | 'incumbent';
  /** Epoch ms after which server will auto-expire the pending takeover decision. */
  expires_at_ms: number;
}

/**
 * Device offline notice — Server → SDK.
 * Additive UI hint emitted when the target device is not reachable: either it was
 * not in the server's client registry at session_open (reason='never_connected'),
 * or its connection dropped mid-session (reason='disconnected'). The server still
 * issues session_bootstrap as usual, so a camera that connects *after* session_open
 * keeps working; this message lets the player surface an immediate "camera offline"
 * state instead of hanging in 'connecting' until a media timeout.
 */
export interface DeviceOfflineMsg {
  type: 'device_offline';
  session_id: string;
  request_id?: string;
  device_serial: string;
  channel_id: string;
  reason: 'never_connected' | 'disconnected';
}

/**
 * Quality adaptation acknowledgement — Streamer/Firmware → Server → SDK.
 * Confirms that the camera has applied a quality_hint command.
 */
export interface QualityAdaptedAckMsg {
  type: 'quality_adapted_ack';
  session_id: string;
  camera_id: number;
  rendition?: string;           // current rendition label after adaptation
  bitrate_kbps?: number;
  fps?: number;
  tier?: 'primary' | 'secondary'; // JT/T1078 only
}

/**
 * Camera QoS telemetry — Streamer/Firmware → Server → SDK.
 * Pushed by camera every ~3–10s. Contains encoder health metrics for congestion
 * detection and freshness tracking. ts_ms is required for freshness evaluation.
 */
export interface CameraQosMsg {
  type: 'camera_qos';
  session_id: string;
  camera_id: number;
  mean_qp?: number;             // mean encoder QP for last GOP; >45 = encoder stressed
  actual_fps?: number;          // firmware-reported output FPS
  applied_profile?: string;     // e.g. "1080p-avbr" or "secondary"
  ts_ms: number;                // epoch ms when measurement was taken — required
}

/**
 * Metrics push — JT/T1078 firmware/relay → Server → SDK.
 * Carries 0x9105 packet-loss data plus custom applied-state telemetry.
 * ts_ms required; stale metrics (age > 2×poll cycle) treated as degraded.
 */
export interface MetricsPushMsg {
  type: 'metrics_push';
  session_id: string;
  channel_id: string;
  packet_loss_rate?: number;    // 0x9105 PacketLossRate (0–100)
  applied_profile?: string;     // current tier/rendition applied
  actual_fps?: number;
  encoder_qp?: number;
  ts_ms: number;                // epoch ms — required for freshness tracking
}

/**
 * Capability report — Streamer → Server (persisted) → SDK.
 * Describes camera inventory, transport support, renditions, and recording-safe floors.
 * Server MUST persist this per device_serial and serve cached copy in session_bootstrap.
 *
 * NOTE: Field names here are DRAFT. Signaling implementation may rename fields
 * but MUST preserve the semantics: transport support/default, rendition caps,
 * recording-safe floors, and telemetry fields. See docs/qos/design-qos.md §Contract Freeze Rules.
 */
export interface CapabilityReportMsg {
  type: 'capability_report';
  session_id: string;
  /** Structured camera list. Each entry represents one physical camera. */
  cameras: Array<{
    camera_id: number;
    /** 4-channel model: channel identifier string e.g. "101", "102". Takes priority over camera_id. */
    channel_id?: string;
    nominal_fps: number;
    transports: Array<{
      family: 'interactive' | 'stream_socket';
      protocol: 'webrtc' | 'jt1078';
      enabled: boolean;
      priority: number;
      default_for_live: boolean;
    }>;
    renditions: Array<{
      label: string;
      width: number;
      height: number;
      fps_min: number;
      fps_max: number;
      bitrate_min_kbps: number;
      bitrate_max_kbps: number;
      /** If true, this rendition shares encoder with recording. */
      shared_from_recording: boolean;
      /** ABR floor to avoid degrading recording quality below safe minimum. */
      recording_safe_min_bitrate_kbps?: number;
      recording_safe_min_fps?: number;
    }>;
    rc_modes: ('CBR' | 'VBR' | 'AVBR')[];
    codecs: ('H264' | 'H265')[];
  }>;
}

// ── List recordings (non-session request/response) ─────────────────────────

export interface ListEventsMsg {
  type: 'list_events';
  request_id: string;
  device_serial: string;
  since_us: number;
  until_us: number;
  max_count: number;
  order: 'asc' | 'desc';
}

export interface EventListEvent {
  event_id?: string;
  ts_us: number;
  camera_mask: number;
  slot_id: number;
  type?: string;
  score?: number;
  pre_roll_ms?: number;
  post_roll_ms?: number;
  duration_ms?: number;
}

export interface EventListMsg {
  type: 'event_list';
  request_id?: string;
  status: 'ok' | 'error';
  message?: string;
  events?: EventListEvent[];
}

// ── Update union types ─────────────────────────────────────────────────────

// Re-export extended unions (consumers should import these, not the old bare unions)

export type ToServerMsgV2 =
  | SessionOpenMsg
  | SessionStartMsg
  | SessionStopMsg
  | SdpAnswerMsg
  | IceCandidateMsg
  | QosReportMsg
  | SessionTakeoverResponseMsg
  | SessionHeartbeatMsg
  | ListEventsMsg;

export type FromServerMsgV2 =
  | SessionBootstrapMsg
  | SdpOfferMsg
  | SessionConflictMsg
  | DeviceOfflineMsg
  | QualityAdaptedAckMsg
  | CameraQosMsg
  | MetricsPushMsg
  | CapabilityReportMsg
  | SessionErrorMsg
  | EventListMsg;
